import { ENDPOINTS, SUBSCRIBER_TOKEN_HEADER } from '../constants';
import { meta } from '../meta';
import type {
  CancellableWidgetRequestArgs,
  Endpoint,
  PaymentMetadata,
  ProductData
} from '../types';
import { isSubscriptionData } from '../types';
import { baseFetch } from './base-fetch';
import { getBaseFetchHeaders } from './get-base-fetch-headers';

type CreateCheckoutSessionArgs = CancellableWidgetRequestArgs & {
  providerIntegrationId: string;
  customerEmail: string;
  productData: ProductData;
  paymentMetadata?: PaymentMetadata;
  locale?: string;
};

type CreateCheckoutSessionPayload = {
  clientSecret: string;
  id: string;
  subscriberToken: string;
};

export function createCheckoutSession({
  providerIntegrationId,
  customerEmail,
  productData,
  widgetToken,
  paymentMetadata,
  locale,
  abortSignal
}: CreateCheckoutSessionArgs) {
  let createSessionEndpoint: Endpoint = ENDPOINTS.createPayment;

  if (isSubscriptionData(productData)) {
    createSessionEndpoint = ENDPOINTS.createSubscription;
  }

  return baseFetch({
    url: `${meta.paymentServiceUrl}/${createSessionEndpoint}`,
    init: {
      method: 'POST',
      headers: getBaseFetchHeaders({ widgetToken, withContentType: true }),
      body: JSON.stringify({
        providerIntegrationId,
        customerEmail,
        lineItems: [productData],
        metadata: paymentMetadata,
        locale
      }),
      signal: abortSignal
    },
    handleResponse: ({ response, handleSuccess, errorMessage }) => {
      return response.json().then(({ code, message, payload }) => {
        if (code === 200 || code === 201 || code === 204) {
          return handleSuccess({
            ...payload,
            subscriberToken: response.headers.get(SUBSCRIBER_TOKEN_HEADER)
          });
        }

        throw new Error(errorMessage || message);
      });
    },
    handleSuccess: ({
      clientSecret,
      id: sessionId,
      subscriberToken
    }: CreateCheckoutSessionPayload) => {
      if (!clientSecret) {
        throw new Error('Received invalid client secret');
      }

      if (!sessionId) {
        throw new Error('Received invalid payment session id');
      }

      if (!subscriberToken) {
        throw new Error('Received invalid subscriber token');
      }

      return { clientSecret, sessionId, subscriberToken };
    },
    errorMessage: 'Failed to create checkout session'
  });
}
